import { get, authGet, authPost, authPut, authDelete } from './apiService';

/**
 * Service for new ADO import system API calls
 */
export const newImportService = {
  /**
   * Test connection to Azure DevOps
   * @returns {Promise} API response
   */
  testConnection: () => {
    return authGet('/api/new-import/test-connection');
  },

  /**
   * Get import statistics
   * @returns {Promise} API response with import stats
   */
  getImportStats: () => {
    return authGet('/api/new-import/stats');
  },

  /**
   * Get import tracker statistics
   * @returns {Promise} API response with tracker stats
   */
  getTrackerStats: () => {
    return authGet('/api/new-import/tracker-stats');
  },

  /**
   * Run full import
   * @param {Object} options - Import options
   * @param {string} options.dateFilter - Date filter for import (e.g., '2024-01-01')
   * @param {boolean} options.clearExisting - Whether to clear existing data
   * @param {Array<string>} options.workItemTypes - Array of work item types to import (optional)
   * @returns {Promise} API response
   */
  runFullImport: (options = {}) => {
    const body = {
      dateFilter: options.dateFilter || '2024-01-01',
      clearExisting: options.clearExisting !== false // Default to true
    };
    if (options.workItemTypes && options.workItemTypes.length > 0) {
      body.workItemTypes = options.workItemTypes;
    }
    return authPost('/api/new-import/full', body);
  },

  /**
   * Run daily update import
   * @param {Object} options - Import options
   * @param {number} options.hoursBack - Hours to look back for changes
   * @param {Array<string>} options.workItemTypes - Array of work item types to import (optional)
   * @returns {Promise} API response
   */
  runDailyUpdate: (options = {}) => {
    const body = {
      hoursBack: options.hoursBack || 30
    };
    if (options.workItemTypes && options.workItemTypes.length > 0) {
      body.workItemTypes = options.workItemTypes;
    }
    return authPost('/api/new-import/daily-update', body);
  },

  /**
   * Import items after a specific date
   * @param {string} afterDate - ISO date string or YYYY-MM-DD format
   * @param {Object} options - Import options
   * @param {Array<string>} options.workItemTypes - Array of work item types to import (optional)
   * @returns {Promise} API response
   */
  importAfterDate: (afterDate, options = {}) => {
    const body = { afterDate };
    if (options.workItemTypes && options.workItemTypes.length > 0) {
      body.workItemTypes = options.workItemTypes;
    }
    return authPost('/api/new-import/after-date', body);
  },

  /**
   * Import specific work item type
   * @param {string} workItemType - Type of work item (epics, features, pbis, bugs, tasks, tests)
   * @param {Object} options - Import options
   * @returns {Promise} API response
   */
  importWorkItemType: (workItemType, options = {}) => {
    return authPost(`/api/new-import/${workItemType}`, options);
  },

  /**
   * Build hierarchies for all epics
   * @returns {Promise} API response
   */
  buildHierarchies: () => {
    return authPost('/api/new-import/build-hierarchies');
  },

  /**
   * Build hierarchies for specific epics
   * @param {Array} epicIds - Array of epic IDs
   * @returns {Promise} API response
   */
  buildHierarchiesForEpics: (epicIds) => {
    return authPost('/api/new-import/build-hierarchies/epics', { epicIds });
  },

  /**
   * Validate hierarchy data integrity
   * @returns {Promise} API response
   */
  validateHierarchy: () => {
    return authGet('/api/new-import/validate-hierarchy');
  },

  /**
   * Repair hierarchy data issues
   * @param {Object} options - Repair options
   * @param {boolean} options.removeOrphans - Whether to remove orphaned items
   * @param {boolean} options.fixParentReferences - Whether to fix parent references
   * @returns {Promise} API response
   */
  repairHierarchy: (options = {}) => {
    return authPost('/api/new-import/repair-hierarchy', {
      removeOrphans: options.removeOrphans || false,
      fixParentReferences: options.fixParentReferences !== false // Default to true
    });
  },

  /**
   * Get work items by type with pagination
   * @param {string} workItemType - Type of work item
   * @param {Object} options - Query options
   * @param {number} options.page - Page number
   * @param {number} options.limit - Items per page
   * @returns {Promise} API response
   */
  getWorkItemsByType: (workItemType, options = {}) => {
    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page);
    if (options.limit) params.append('limit', options.limit);
    
    const queryString = params.toString();
    const url = `/api/new-import/work-items/${workItemType}${queryString ? `?${queryString}` : ''}`;
    
    return authGet(url);
  },

  /**
   * Run full import for specific work item type
   * @param {string} workItemType - Type of work item (epics, features, pbis, bugs, tasks, tests)
   * @param {Object} options - Import options
   * @param {string} options.dateFilter - Date filter for import
   * @returns {Promise} API response
   */
  runFullImportForType: (workItemType, options = {}) => {
    return authPost(`/api/new-import/${workItemType}`, {
      dateFilter: options.dateFilter || '2024-01-01',
      importType: 'full'
    });
  },

  /**
   * Run delta import for specific work item type
   * @param {string} workItemType - Type of work item (epics, features, pbis, bugs, tasks, tests)
   * @param {Object} options - Import options
   * @param {number} options.hoursBack - Hours to look back for changes
   * @returns {Promise} API response
   */
  runDeltaImportForType: (workItemType, options = {}) => {
    return authPost(`/api/new-import/${workItemType}`, {
      hoursBack: options.hoursBack || 30,
      importType: 'delta'
    });
  },

  /**
   * Run nightly delta import for all work item types
   * @param {Object} options - Import options
   * @param {number} options.hoursBack - Hours to look back for changes
   * @returns {Promise} API response
   */
  runNightlyDeltaImport: (options = {}) => {
    return authPost('/api/new-import/nightly-delta', {
      hoursBack: options.hoursBack || 30
    });
  },

  /**
   * Get import logs with pagination and filtering
   * @param {Object} options - Query options
   * @param {number} options.limit - Number of logs to retrieve
   * @param {string} options.type - Filter by import type
   * @param {string} options.status - Filter by status
   * @returns {Promise} API response
   */
  getImportLogs: (options = {}) => {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.type) params.append('type', options.type);
    if (options.status) params.append('status', options.status);
    
    const queryString = params.toString();
    const url = `/api/new-import/logs${queryString ? `?${queryString}` : ''}`;
    
    return authGet(url);
  },

  /**
   * Get specific import log by ID
   * @param {string} logId - Import log ID
   * @returns {Promise} API response
   */
  getImportLogById: (logId) => {
    return authGet(`/api/new-import/logs/${logId}`);
  },

  /**
   * Poll import status (alias for getImportLogById for consistency)
   * @param {string} logId - Import log ID
   * @returns {Promise} API response
   */
  pollImportStatus: (logId) => {
    return authGet(`/api/new-import/logs/${logId}`);
  },

  /**
   * Get current running import status
   * @returns {Promise} API response
   */
  getCurrentImportStatus: () => {
    return authGet('/api/new-import/status');
  },

  /**
   * Import SHA Items with full/delta support
   * @param {Object} options - Import options
   * @param {string} options.importType - Type of import ('full' or 'delta')
   * @param {string} options.dateFilter - Date filter for full import (e.g., '2024-01-01')
   * @param {number} options.hoursBack - Hours to look back for delta import
   * @returns {Promise} API response
   */
  importShaItems: (options = {}) => {
    return authPost('/api/new-import/shaitems', {
      importType: options.importType || 'delta',
      dateFilter: options.dateFilter || '2024-01-01',
      hoursBack: options.hoursBack || 30
    });
  },

  /**
   * Import SHA Items from CSV file
   * @param {FormData} formData - FormData containing CSV file
   * @returns {Promise} API response
   */
  importShaItemsFromCsv: (formData) => {
    // Get token from localStorage
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Authentication required');
    }

    // Make the request directly with fetch to handle FormData properly
    return fetch('/api/new-import/sha-items/csv-upload', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
        // Don't set Content-Type - let browser set it automatically for FormData
      },
      body: formData
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json();
        
        // Check for session timeout
        if (errorData.error === 'session_timeout') {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = `/login?session_timeout=true&redirect=${encodeURIComponent(window.location.pathname)}`;
          throw new Error('Your session has timed out. Please log in again.');
        }
        
        throw new Error(errorData.message || `Upload failed with status: ${response.status}`);
      }
      
      return response.json();
    });
  },

  /**
   * Get SHA automation by month chart data
   * @returns {Promise} API response
   */
  getShaAutomationByMonth: () => {
    return authGet('/api/new-import/sha-charts/automation-by-month');
  },

  /**
   * Get SHA EPAM identification chart data
   * @returns {Promise} API response
   */
  getShaEpamIdentification: () => {
    return authGet('/api/new-import/sha-charts/epam-identification');
  },

  /**
   * Get scheduler status
   * @returns {Promise} API response
   */
  getSchedulerStatus: () => {
    return authGet('/api/new-import/scheduler/status');
  },

  /**
   * Enable scheduler
   * @param {Object} options - Scheduler options
   * @param {string} options.schedule - Cron schedule (optional, for backward compatibility)
   * @param {Object} options.scheduleConfig - Schedule configuration with days and time
   * @param {Array<number>} options.scheduleConfig.days - Array of day numbers (0=Sunday, 6=Saturday)
   * @param {Object} options.scheduleConfig.time - Time object with hour and minute
   * @returns {Promise} API response
   */
  enableScheduler: (options = {}) => {
    const body = {};
    if (options.scheduleConfig) {
      body.scheduleConfig = options.scheduleConfig;
    } else {
      body.schedule = options.schedule || '0 2 * * *';
    }
    return authPost('/api/new-import/scheduler/enable', body);
  },

  /**
   * Disable scheduler
   * @returns {Promise} API response
   */
  disableScheduler: () => {
    return authPost('/api/new-import/scheduler/disable');
  },

  /**
   * Update scheduler schedule
   * @param {string|Object} scheduleOrConfig - Cron schedule string or scheduleConfig object
   * @returns {Promise} API response
   */
  updateSchedulerSchedule: (scheduleOrConfig) => {
    const body = typeof scheduleOrConfig === 'object' 
      ? { scheduleConfig: scheduleOrConfig }
      : { schedule: scheduleOrConfig };
    return authPut('/api/new-import/scheduler/schedule', body);
  },

  /**
   * Get console logs for a specific import log
   * @param {string} logId - Import log ID
   * @param {Object} options - Query options
   * @param {number} options.page - Page number
   * @param {number} options.limit - Items per page
   * @param {string} options.level - Log level filter
   * @returns {Promise} API response
   */
  getConsoleLogsById: (logId, options = {}) => {
    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page);
    if (options.limit) params.append('limit', options.limit);
    if (options.level) params.append('level', options.level);
    
    const queryString = params.toString();
    const url = `/api/new-import/logs/${logId}/console-logs${queryString ? `?${queryString}` : ''}`;
    
    return authGet(url);
  },

  /**
   * Delete console logs for a specific import log
   * @param {string} logId - Import log ID
   * @returns {Promise} API response
   */
  deleteConsoleLogsById: (logId) => {
    return authDelete(`/api/new-import/logs/${logId}/console-logs`);
  },

  /**
   * Delete an import log
   * @param {string} logId - Import log ID
   * @returns {Promise} API response
   */
  deleteImportLog: (logId) => {
    return authDelete(`/api/new-import/logs/${logId}`);
  },

  // ===== EPIC-BASED IMPORT METHODS =====

  /**
   * Run full epic import
   * @param {Object} options - Import options
   * @param {boolean} options.clearExisting - Whether to clear existing data
   * @returns {Promise} API response
   */
  runFullEpicImport: (options = {}) => {
    return authPost('/api/import/epic-import/full', {
      clearExisting: options.clearExisting !== false // Default to true
    });
  },

  /**
   * Run delta epic import
   * @returns {Promise} API response
   */
  runDeltaEpicImport: () => {
    return authPost('/api/import/epic-import/delta');
  },


  /**
   * Get epic import status
   * @returns {Promise} API response
   */
  getEpicImportStatus: () => {
    return authGet('/api/import/epic-import/status');
  },

  /**
   * Get epic import statistics
   * @returns {Promise} API response
   */
  getEpicImportStats: () => {
    return authGet('/api/import/epic-import/stats');
  },

  /**
   * Test ADO connection for epic import
   * @returns {Promise} API response
   */
  testEpicImportConnection: () => {
    return get('/api/import/epic-import/test-connection');
  },

  /**
   * Get epic import progress
   * @returns {Promise} API response
   */
  getEpicImportProgress: () => {
    return authGet('/api/import/epic-import/progress');
  },

  /**
   * Cancel epic import
   * @returns {Promise} API response
   */
  cancelEpicImport: () => {
    return authPost('/api/import/epic-import/cancel');
  },

  /**
   * Get import history (last 25 by default)
   * @param {number} limit - Number of import logs to retrieve
   * @returns {Promise} API response
   */
  getImportHistory: (limit = 25) => {
    return authGet(`/api/import/epic-import/history?limit=${limit}`);
  },

  /**
   * Get specific import log details
   * @param {string} logId - Import log ID
   * @returns {Promise} API response
   */
  getImportLogDetails: (logId) => {
    return authGet(`/api/import/epic-import/logs/${logId}`);
  },

  /**
   * Get/Save Epic WIQL (admin editable)
   */
  getEpicWiql: () => authGet('/api/import/epic-import/wiql'),
  saveEpicWiql: (epicWiql) => authPost('/api/import/epic-import/wiql', { epicWiql }),

  /**
   * Poll import status
   * @param {string} logId - Import log ID
   * @returns {Promise} API response
   */
  pollImportStatus: (logId) => {
    return authGet(`/api/import/epic-import/logs/${logId}`);
  },

  /**
   * Rebuild hierarchies from WorkItem collection
   * @returns {Promise} API response
   */
  rebuildHierarchies: () => {
    return authPost('/api/workitems/build-hierarchies-from-workitems');
  },

  // ===== ADO FIELD MAPPING METHODS =====

  /**
   * Get ADO field mapping configuration
   * @returns {Promise} API response
   */
  getAdoFieldMapping: () => {
    return authGet('/api/import/ado-field-mapping');
  },

  /**
   * Save ADO field mapping configuration
   * @param {Object} fieldMapping - Field mapping configuration
   * @returns {Promise} API response
   */
  saveAdoFieldMapping: (fieldMapping) => {
    return authPost('/api/import/ado-field-mapping', fieldMapping);
  },

  /**
   * Test ADO field availability
   * @param {Array} fields - Array of field names to test
   * @returns {Promise} API response
   */
  testAdoFieldMapping: (fields) => {
    return authPost('/api/import/ado-field-mapping/test', { fields });
  },

  /**
   * Get available fields from ADO
   * @returns {Promise} API response
   */
  getAvailableAdoFields: () => {
    return authGet('/api/import/ado-field-mapping/available-fields');
  },

  /**
   * Reset ADO field mapping to default
   * @returns {Promise} API response
   */
  resetAdoFieldMapping: () => {
    return authPost('/api/import/ado-field-mapping/reset');
  },

  /**
   * Get Full Import field defaults (for Mapping tab)
   * @returns {Promise} API response with { commonFields, workItemTypeFields }
   */
  getFullImportFieldDefaults: () => {
    return authGet('/api/import/ado-field-mapping/full-import-defaults');
  },

  /**
   * Get Full Import display config (Work Items, Test, RCA, Tech Debt tables)
   * @returns {Promise} API response
   */
  getFullImportDisplayConfig: () => {
    return authGet('/api/import/ado-field-mapping/full-import-display');
  },

  /**
   * Reset field mapping to Full Import defaults and save
   * @returns {Promise} API response
   */
  resetAdoFieldMappingToFullImport: () => {
    return authPost('/api/import/ado-field-mapping/reset-to-full-import');
  },

  // ===== TEST IMPORT METHODS =====

  /**
   * Run full test import
   * @param {Object} options - Import options
   * @returns {Promise} API response
   */
  runFullTestImport: (options = {}) => {
    return authPost('/api/import/test-import/full', options);
  },

  /**
   * Run delta test import
   * @returns {Promise} API response
   */
  runDeltaTestImport: () => {
    return authPost('/api/import/test-import/delta');
  },

  /**
   * Get test import statistics
   * @returns {Promise} API response
   */
  getTestImportStats: () => {
    return authGet('/api/import/test-import/stats');
  },

  // ===== QUICK UPDATES METHODS =====

  /**
   * Get available fields for quick updates
   * @returns {Promise} API response
   */
  getQuickUpdateAvailableFields: () => {
    return authGet('/api/import/quick-updates/available-fields');
  },

  /**
   * Get work item counts by collection
   * @returns {Promise} API response
   */
  getQuickUpdateCounts: () => {
    return authGet('/api/import/quick-updates/counts');
  },

  /**
   * Update work items with selected fields
   * @param {Object} options - Update options
   * @param {Array} options.fields - Array of ADO field names to update
   * @param {Array} options.workItemTypes - Array of workItemType names to update (Epic, Feature, Product Backlog Item, Bug, Task, Test)
   * @returns {Promise} API response
   */
  performQuickUpdate: (options) => {
    return authPost('/api/import/quick-updates/update', {
      fields: options.fields || [],
      workItemTypes: options.workItemTypes || []
    });
  },

  // ===== ADO FETCH METHODS =====

  /**
   * Get ADO configuration including query URL
   * @returns {Promise} API response
   */
  getAdoConfig: () => {
    return authGet('/api/admin/ado-config');
  },

  /**
   * Update ADO configuration including query URL
   * @param {Object} config - ADO configuration
   * @returns {Promise} API response
   */
  updateAdoConfig: (config) => {
    return authPut('/api/admin/ado-config', config);
  },

  /**
   * Get the Sprint Review KPI refresh interval (admin-accessible; superadmin not required).
   * @returns {Promise} { success, data: { kpiRefreshIntervalHours } }
   */
  getSprintKpiConfig: () => {
    return authGet('/api/admin/sprint-kpi-config');
  },

  /**
   * Update the Sprint Review KPI refresh interval.
   * @param {number} kpiRefreshIntervalHours
   * @returns {Promise} { success, data: { kpiRefreshIntervalHours } }
   */
  updateSprintKpiConfig: (kpiRefreshIntervalHours) => {
    return authPut('/api/admin/sprint-kpi-config', { kpiRefreshIntervalHours });
  },

  getAccessTokensAdo: () => authGet('/api/admin/access-tokens/ado'),
  getAccessTokensGithub: () => authGet('/api/admin/access-tokens/github'),
  putAccessTokensGithub: (body) => authPut('/api/admin/access-tokens/github', body),

  /**
   * Get closed epics for archive (optional date filter)
   * @param {Object} params - Optional query params
   * @param {string} params.closedAfter - ISO date string
   * @param {string} params.closedBefore - ISO date string
   * @returns {Promise} API response with data array of { id, title, state, closedDate, team }
   */
  getClosedEpics: (params = {}) => {
    const q = new URLSearchParams();
    if (params.closedAfter) q.set('closedAfter', params.closedAfter);
    if (params.closedBefore) q.set('closedBefore', params.closedBefore);
    const query = q.toString();
    return authGet(`/api/admin/archive/closed-epics${query ? `?${query}` : ''}`);
  },

  /**
   * Move selected epics and their descendants to ArchivedWorkItem collection
   * @param {string[]} epicIds - Array of epic id strings
   * @returns {Promise} API response
   */
  archiveEpics: (epicIds) => {
    return authPost('/api/admin/archive/move', { epicIds });
  },

  /**
   * Discover fields from configured ADO query
   * @returns {Promise} API response
   */
  discoverFieldsFromQuery: () => {
    return authPost('/api/admin/ado-fetch/discover-fields');
  },

  /**
   * Run monthly batch import from Jan 1, 2025
   * @returns {Promise} API response
   */
  runMonthlyBatchImport: () => {
    return authPost('/api/admin/ado-fetch/import');
  },

  /**
   * Get monthly date ranges
   * @returns {Promise} API response
   */
  getMonthlyRanges: () => {
    return authGet('/api/admin/ado-fetch/monthly-ranges');
  },

  // ===== DELTA IMPORT METHODS =====

  /**
   * Run delta import using query-based approach
   * @param {Object} options - Import options
   * @param {number} options.hoursBack - Hours to look back (default: 3)
   * @returns {Promise} API response
   */
  runDeltaImport: (options = {}) => {
    return authPost('/api/admin/ado-fetch/delta-import', options);
  },

  /**
   * Get delta import status (last run, next run, cron schedule)
   * @returns {Promise} API response
   */
  getDeltaImportStatus: () => {
    return authGet('/api/admin/ado-fetch/delta-import/status');
  },

  /**
   * Update delta import configuration (cron schedule, hoursBack)
   * @param {Object} config - Configuration object
   * @param {string} config.cronSchedule - Cron expression
   * @param {number} config.hoursBack - Hours to look back
   * @returns {Promise} API response
   */
  updateDeltaImportConfig: (config) => {
    return authPut('/api/admin/ado-fetch/delta-import/config', config);
  },

  // ===== TEST IMPORT METHODS =====

  /**
   * Discover fields from test query URL
   * @returns {Promise} API response
   */
  discoverTestFieldsFromQuery: () => {
    return authPost('/api/admin/ado-fetch/test-import/discover-fields');
  },

  /**
   * Run monthly batch import for tests
   * @returns {Promise} API response
   */
  runTestMonthlyBatchImport: () => {
    return authPost('/api/admin/ado-fetch/test-import/monthly');
  },

  /**
   * Run delta import for tests
   * @param {Object} options - Import options
   * @param {number} options.hoursBack - Hours to look back (default: 3)
   * @returns {Promise} API response
   */
  runTestDeltaImport: (options = {}) => {
    return authPost('/api/admin/ado-fetch/test-import/delta', options);
  },

  /**
   * Import tests changed/created after specified date
   * @param {string} date - Date string (YYYY-MM-DD)
   * @returns {Promise} API response
   */
  runTestImportAfterDate: (date) => {
    return authPost('/api/admin/ado-fetch/test-import/after-date', { date });
  },

  /**
   * Get test import status (last run, cron schedule, query URL)
   * @returns {Promise} API response
   */
  getTestImportStatus: () => {
    return authGet('/api/admin/ado-fetch/test-import/status');
  },

  /**
   * Update test import configuration (query URL, cron schedule, hoursBack)
   * @param {Object} config - Configuration object
   * @param {string} config.queryUrl - Test query URL
   * @param {string} config.cronSchedule - Cron expression
   * @param {number} config.hoursBack - Hours to look back
   * @returns {Promise} API response
   */
  updateTestImportConfig: (config) => {
    return authPut('/api/admin/ado-fetch/test-import/config', config);
  },

  /**
   * Calculate and update automatedOn dates for imported tests
   * @param {Object} options - Options for calculation
   * @param {boolean} options.onlyMissing - Only update tests without automatedOn date
   * @returns {Promise} API response
   */
  calculateAutomatedOnDates: (options = {}) => {
    return authPost('/api/admin/ado-fetch/test-import/calculate-automated-on', options);
  },

  /**
   * Update a single ADO work item and all its descendants (fetch from ADO, save to DB, rebuild hierarchy)
   * @param {string|number} workItemId - ADO work item ID (any type)
   * @returns {Promise} API response with created, updated, errors, byType, hierarchies
   */
  updateSingleItemFromAdo: (workItemId) => {
    return authPost('/api/admin/ado-fetch/update-single-item', { workItemId: String(workItemId) });
  },

  /**
   * Update work items from an ADO filter URL: resolve URL to query, fetch all matching items, save to portal
   * @param {string} filterUrl - ADO filter/query URL (e.g. .../_queries/query/{guid} or .../_workitems?wiql=...)
   * @returns {Promise} API response with created, updated, errors, byType, totalProcessed
   */
  updateFromFilterFromAdo: (filterUrl) => {
    return authPost('/api/admin/ado-fetch/update-from-filter', { filterUrl: String(filterUrl || '').trim() });
  }
};

export default newImportService;
